// AdminRouteTracking.jsx — Patrol Site Live Tracking
//
// FIXES in this version:
//
//  FIX-5: isCompleted broken array trick removed.
//         Old: ["EARLY","ONTIME","DELAY","COMPLETED","INPROGRESS".split(",")[99]||"COMPLETED"]
//         "INPROGRESS".split(",")[99] = undefined → "COMPLETED", so isCompleted was true
//         even during an active patrol → live user marker was immediately removed.
//         Fix: explicit array ["EARLY","ONTIME","DELAY","COMPLETED"] only.
//
//  FIX-6: animateMarker threshold reduced from 0.000009° (~1m) to 0.0000001° (~1cm).
//         At 1m threshold a guard walking slowly never triggered the animation —
//         the marker froze in place while the polyline kept growing.
//
//  FIX-7: catmullRomSpline capped at 4 segments when point count > 500.
//         With 2000 GPS points × 8 segments = 16,000 Leaflet vertices causing
//         visible lag on pan/zoom. Douglas-Peucker simplification added before
//         the spline for large tracks.
//
//  FIX-8: Static layer refs nulled out in useEffect cleanup to prevent stale
//         references if component is remounted on the same DOM node.

import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import Layout from "../layouts/Layout";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const API_BASE_URL = "https://patrol-react.eblackdog.net";

const ICON = {
  pinSizeFrom: 44,
  pinSizeTo:   44,
  userSize:    48,
  cpRadius:    9,
};

const GAP_MINUTES         = 5;
const GAP_MS              = GAP_MINUTES * 60 * 1000;
const MAX_JUMP_METERS     = 300;
const MAX_SPEED_MPS       = 25;
const MAX_ACCURACY_METERS = 20;

const POLL_FAST_MS = 3000;
const POLL_SLOW_MS = 8000;

// FIX-5: explicit set — INPROGRESS is NOT in here
const COMPLETED_STATUSES = new Set(["EARLY", "ONTIME", "DELAY", "COMPLETED"]);

// ── Douglas-Peucker simplification (FIX-7) ───────────────────────────────────
function perpendicularDist(pt, lineStart, lineEnd) {
  const [x, y]   = pt;
  const [x1, y1] = lineStart;
  const [x2, y2] = lineEnd;
  const dx = x2 - x1, dy = y2 - y1;
  if (dx === 0 && dy === 0) return Math.hypot(x - x1, y - y1);
  const t  = ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy);
  return Math.hypot(x - (x1 + t * dx), y - (y1 + t * dy));
}

function rdpSimplify(points, epsilon) {
  if (points.length < 3) return points;
  let maxDist = 0, maxIdx = 0;
  for (let i = 1; i < points.length - 1; i++) {
    const d = perpendicularDist(points[i], points[0], points[points.length - 1]);
    if (d > maxDist) { maxDist = d; maxIdx = i; }
  }
  if (maxDist > epsilon) {
    const left  = rdpSimplify(points.slice(0, maxIdx + 1), epsilon);
    const right = rdpSimplify(points.slice(maxIdx), epsilon);
    return [...left.slice(0, -1), ...right];
  }
  return [points[0], points[points.length - 1]];
}

// ── Catmull-Rom spline ────────────────────────────────────────────────────────
// FIX-7: auto-reduce numSegments for large point sets
function catmullRomSpline(points, numSegments = 6) {
  if (points.length < 2) return points;

  // For large point sets, simplify first then spline with fewer segments
  let pts = points;
  if (points.length > 300) {
    // epsilon in degrees: ~0.000009 ≈ 1m; use 0.00003 (~3m) for simplification
    pts = rdpSimplify(points, 0.00003);
    numSegments = 4;
  }

  const result = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(i - 1, 0)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(i + 2, pts.length - 1)];
    for (let t = 0; t < numSegments; t++) {
      const tt  = t / numSegments;
      const tt2 = tt * tt;
      const tt3 = tt2 * tt;
      const lat = 0.5 * (2*p1[0] + (-p0[0]+p2[0])*tt + (2*p0[0]-5*p1[0]+4*p2[0]-p3[0])*tt2 + (-p0[0]+3*p1[0]-3*p2[0]+p3[0])*tt3);
      const lng = 0.5 * (2*p1[1] + (-p0[1]+p2[1])*tt + (2*p0[1]-5*p1[1]+4*p2[1]-p3[1])*tt2 + (-p0[1]+3*p1[1]-3*p2[1]+p3[1])*tt3);
      result.push([lat, lng]);
    }
  }
  result.push(pts[pts.length - 1]);
  return result;
}

// ── Smooth marker animation ───────────────────────────────────────────────────
// FIX-6: threshold reduced from 0.000009 to 0.0000001 so slow walking still animates
function animateMarker(marker, targetLatLng, durationMs = 900) {
  const start    = marker.getLatLng();
  const startLat = start.lat;
  const startLng = start.lng;
  const [endLat, endLng] = targetLatLng;
  const dLat = endLat - startLat;
  const dLng = endLng - startLng;
  if (Math.abs(dLat) < 0.0000001 && Math.abs(dLng) < 0.0000001) return;
  const startTime = performance.now();
  function step(now) {
    const elapsed = now - startTime;
    const raw = Math.min(elapsed / durationMs, 1);
    const t   = raw < 0.5 ? 4*raw*raw*raw : 1 - Math.pow(-2*raw+2, 3)/2;
    marker.setLatLng([startLat + dLat*t, startLng + dLng*t]);
    if (raw < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

function pinSVG(fill = "#111827", stroke = "#111827", size = 40) {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="${stroke}" stroke-width="1.5" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22s7-6.33 7-12a7 7 0 1 0-14 0c0 5.67 7 12 7 12z" fill="${fill}" opacity="0.14"/>
    <circle cx="12" cy="10" r="3.25" fill="${fill}"/>
    <path d="M12 22s7-6.33 7-12a7 7 0 1 0-14 0c0 5.67 7 12 7 12z"/>
  </svg>`;
}

function userSVG(color = "#2563EB", size = 44) {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="${color}" stroke-width="1.5" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="8.5" r="3.5" fill="${color}" opacity="0.12"/>
    <path d="M4 19a8 8 0 0 1 16 0" fill="${color}" opacity="0.08"/>
    <circle cx="12" cy="8.5" r="3.5"/>
    <path d="M4 19a8 8 0 0 1 16 0"/>
  </svg>`;
}

function pinIcon(fill, stroke, size, className = "pin") {
  return L.divIcon({ className, html: pinSVG(fill, stroke, size), iconSize: [size, size], iconAnchor: [size/2, size] });
}

function userIcon(color, size, className = "userIcon") {
  return L.divIcon({ className, html: `<div class="pulse"></div>${userSVG(color, size)}`, iconSize: [size, size], iconAnchor: [size/2, size/2] });
}

function badgeIcon(text, bg) {
  return L.divIcon({
    className: "",
    html: `<div style="transform:translate(-50%,-50%);background:${bg};color:#fff;font-size:10px;font-weight:600;padding:2px 5px;border-radius:999px;border:1px solid rgba(0,0,0,.25);box-shadow:0 1px 3px rgba(0,0,0,.25);white-space:nowrap;">${text}</div>`,
    iconSize: [30, 18], iconAnchor: [15, 9],
  });
}

function loginIcon()  { return badgeIcon("LOGIN",  "#16a34a"); }
function logoutIcon() { return badgeIcon("LOGOUT", "#8b5cf6"); }

function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat), lat2 = toRad(b.lat);
  const s = Math.sin(dLat/2)**2 + Math.cos(lat1)*Math.cos(lat2)*Math.sin(dLng/2)**2;
  return 2*R*Math.atan2(Math.sqrt(s), Math.sqrt(1-s));
}

function insideCircle(lat, lng, clat, clng, r) {
  return haversineMeters({lat, lng}, {lat: clat, lng: clng}) <= Number(r||0);
}

function pointInPolygon(lat, lng, ring) {
  let inside = false;
  for (let i = 0, j = ring.length-1; i < ring.length; j = i++) {
    const [latI,lngI] = ring[i], [latJ,lngJ] = ring[j];
    const intersect = lngI>lng !== lngJ>lng && lat < ((latJ-latI)*(lng-lngI))/((lngJ-lngI)||1e-12)+latI;
    if (intersect) inside = !inside;
  }
  return inside;
}

function parseDbIst(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts !== "string") return null;
  const s = ts.trim();
  if (!s) return null;
  const clean = s.replace("T"," ").replace("Z","").split(".")[0];
  const [datePart, timePart] = clean.split(" ");
  if (!datePart || !timePart) return null;
  const [year,month,day]        = datePart.split("-").map(Number);
  const [hour,minute,second=0]  = timePart.split(":").map(Number);
  if (!Number.isFinite(year)||!Number.isFinite(month)||!Number.isFinite(day)||!Number.isFinite(hour)||!Number.isFinite(minute)) return null;
  const d = new Date(year, month-1, day, hour, minute, second);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateObj(d) {
  if (!d) return "—";
  const day=String(d.getDate()).padStart(2,"0"), month=d.toLocaleString("en-IN",{month:"short"}), year=d.getFullYear();
  let hour=d.getHours(); const minute=String(d.getMinutes()).padStart(2,"0"), second=String(d.getSeconds()).padStart(2,"0");
  const ampm=hour>=12?"pm":"am"; hour=hour%12||12;
  return `${day} ${month} ${year}, ${String(hour).padStart(2,"0")}:${minute}:${second} ${ampm}`;
}

function formatISTDateTime(ts) { const d=parseDbIst(ts); return d ? formatDateObj(d) : ts||"—"; }

function formatISTFromUtc(ts) {
  if (!ts) return "—";
  if (ts instanceof Date) {
    if (Number.isNaN(ts.getTime())) return "—";
    return new Intl.DateTimeFormat("en-IN",{timeZone:"Asia/Kolkata",day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit",second:"2-digit",hour12:true}).format(ts).replace(",","");
  }
  if (typeof ts !== "string") return "—";
  const s=ts.trim(); if(!s) return "—";
  const utcStr=/Z$|[+-]\d{2}:?\d{2}$/.test(s)?s:`${s}Z`;
  const d=new Date(utcStr); if(Number.isNaN(d.getTime())) return ts||"—";
  return new Intl.DateTimeFormat("en-IN",{timeZone:"Asia/Kolkata",day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit",second:"2-digit",hour12:true}).format(d).replace(",","");
}

function isValidLatLng(p) { return p && Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)); }

function cleanTrack(rawTrack) {
  const ordered = rawTrack
    .filter(p => isValidLatLng(p))
    .map(p => ({ ...p, lat: Number(p.lat), lng: Number(p.lng), accuracy: p.accuracy==null?null:Number(p.accuracy) }))
    .filter(p => p.accuracy==null || p.accuracy <= MAX_ACCURACY_METERS)
    .sort((a,b) => (parseDbIst(a.ts)?.getTime()??0) - (parseDbIst(b.ts)?.getTime()??0));

  const cleaned = [];
  for (const p of ordered) {
    if (!cleaned.length) { cleaned.push(p); continue; }
    const prev   = cleaned[cleaned.length-1];
    if (prev.lat===p.lat && prev.lng===p.lng && prev.ts===p.ts) continue;
    const dist   = haversineMeters({lat:prev.lat,lng:prev.lng},{lat:p.lat,lng:p.lng});
    const prevMs = parseDbIst(prev.ts)?.getTime()??null;
    const curMs  = parseDbIst(p.ts)?.getTime()??null;
    const seconds= prevMs!=null&&curMs!=null ? Math.max(1,(curMs-prevMs)/1000) : 1;
    const speed  = dist/seconds;
    if (dist > MAX_JUMP_METERS && speed > MAX_SPEED_MPS) {
      console.log(`[MAP] Reject teleport dist=${dist.toFixed(1)}m speed=${speed.toFixed(1)}m/s`);
      continue;
    }
    cleaned.push(p);
  }
  return cleaned;
}

function getLocalDashboardData() {
  const userData = JSON.parse(localStorage.getItem("userData")||"{}");
  return {
    company_name:    localStorage.getItem("company_name")    || userData.company_name    || "",
    licensedFor:     localStorage.getItem("licensedFor")     || userData.licensedFor     || "",
    admin_active:    Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
    company_address: localStorage.getItem("company_address") || userData.company_address || "",
    company_phone:   localStorage.getItem("company_phone")   || userData.company_phone   || "",
    admin_email:     localStorage.getItem("admin_email")     || userData.admin_email     || "",
    company_logo:    localStorage.getItem("company_logo")    || userData.company_logo    || "",
    user_map:        localStorage.getItem("user_map")        || userData.user_map        || "",
  };
}

export default function AdminRouteTracking() {
  const location = useLocation();

  const mapRef              = useRef(null);
  const latestMarkerRef     = useRef(null);
  const userPathLayerRef    = useRef(null);
  const loginLogoutLayerRef = useRef(null);
  const staticLayersRef     = useRef(false);
  const didInitialFitRef    = useRef(false);
  const checkpointsLayerRef = useRef(null);
  const geofenceLayerRef    = useRef(null);
  const routeLineLayerRef   = useRef(null);
  const routeEndPointsRef   = useRef(null);

  const pollTimeoutRef          = useRef(null);
  const currentPollIntervalRef  = useRef(POLL_FAST_MS);
  const lastPositionKeyRef      = useRef("");
  const isFetchingRef           = useRef(false);
  const lastTrackSigRef         = useRef("");

  const dashboardData = useMemo(() => getLocalDashboardData(), []);
  const routePage     = useMemo(() => dashboardData.user_map==="google" ? "/route-master" : "/route-master1", [dashboardData.user_map]);

  const queryData = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      routeId: Number(params.get("route_id")||0),
      userId:  params.get("user_id")||"",
      allocId: Number(params.get("alloc_id")||0),
    };
  }, [location.search]);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams({ route_id: String(queryData.routeId), limit: "2000" });
    if (queryData.userId)  params.set("user_id",  queryData.userId);
    if (queryData.allocId) params.set("alloc_id", String(queryData.allocId));
    return `${API_BASE_URL}/api/routeTracking?${params.toString()}`;
  }, [queryData]);

  const setStatus = useCallback((id, text, type="neutral") => {
    const el = document.getElementById(id);
    if (!el) return;
    el.textContent = text;
    const base="px-3 py-1 text-xs font-semibold rounded-full";
    const classes={ok:"bg-green-100 text-green-700",warn:"bg-red-100 text-red-700",neutral:"bg-slate-200 text-slate-800"};
    el.className=`${base} ${classes[type]||classes.neutral}`;
  }, []);

  const statusSet      = useCallback((t,c="neutral")=>setStatus("fenceStatus",t,c),[setStatus]);
  const routeStatusSet = useCallback((t,c="neutral")=>setStatus("routeStatus",t,c),[setStatus]);

  const applyCompletionStatus = useCallback(completion => {
    if (!completion?.status) { routeStatusSet("Route status: —","neutral"); return; }
    const status=String(completion.status||"").toUpperCase(), label=completion.label||"";
    let cls="neutral";
    if(["EARLY","ONTIME","COMPLETED"].includes(status)) cls="ok";
    if(["DELAY","PENDING_DELAY"].includes(status))      cls="warn";
    routeStatusSet(label||`Route status: ${status}`, cls);
  }, [routeStatusSet]);

  const drawStaticLayers = useCallback(data => {
    const map = mapRef.current;
    if (!map || staticLayersRef.current) return;

    const bounds = L.latLngBounds();
    const from   = data.route?.meta?.from || null;
    const to     = data.route?.meta?.to   || null;
    const endMarkers = [];

    if (isValidLatLng(from)) {
      const lat=Number(from.lat), lng=Number(from.lng);
      endMarkers.push(L.marker([lat,lng],{icon:pinIcon("#16a34a","#166534",ICON.pinSizeFrom)}).bindTooltip("From",{direction:"top"}));
      bounds.extend([lat,lng]);
    }
    if (isValidLatLng(to)) {
      const lat=Number(to.lat), lng=Number(to.lng);
      endMarkers.push(L.marker([lat,lng],{icon:pinIcon("#dc2626","#991b1b",ICON.pinSizeTo)}).bindTooltip("To",{direction:"top"}));
      bounds.extend([lat,lng]);
    }
    if (endMarkers.length) routeEndPointsRef.current = L.layerGroup(endMarkers).addTo(map);

    const routeLine = data.route?.line?.geometry?.coordinates||[];
    if (Array.isArray(routeLine) && routeLine.length>=2) {
      const coordsLL = routeLine.map(([lng,lat])=>[Number(lat),Number(lng)]).filter(([la,ln])=>Number.isFinite(la)&&Number.isFinite(ln));
      if (coordsLL.length>=2) {
        routeLineLayerRef.current = L.polyline(coordsLL,{color:"#0ea5e9",weight:4,opacity:0.75,dashArray:"5,7"}).addTo(map);
        coordsLL.forEach(c=>bounds.extend(c));
      }
    }

    const cps = Array.isArray(data.checkpoints) ? data.checkpoints : [];
    const cpMarkers = cps.filter(cp=>isValidLatLng(cp)).map(cp=>{
      const lat=Number(cp.lat), lng=Number(cp.lng);
      bounds.extend([lat,lng]);
      return L.circleMarker([lat,lng],{radius:ICON.cpRadius,color:"#16a34a",weight:2,opacity:0.95,fillColor:"#22c55e",fillOpacity:0.35})
        .bindTooltip(cp.checkpoint_name||"Checkpoint",{direction:"top"});
    });
    if (cpMarkers.length) checkpointsLayerRef.current = L.layerGroup(cpMarkers).addTo(map);

    const gf=data.geofences?.features||[];
    const gfShapes=[];
    if (Array.isArray(gf)&&gf.length) {
      for (const f of gf) {
        const p=f.properties||{};
        if (p.type==="CIRCLE"&&f.geometry?.type==="Point") {
          const [lngR,latR]=f.geometry.coordinates||[];
          const lat=Number(latR), lng=Number(lngR);
          if (Number.isFinite(lat)&&Number.isFinite(lng)) {
            gfShapes.push(L.circle([lat,lng],{radius:Number(p.radius_m||0),color:"#f59e0b",weight:2,fillOpacity:0.08}).bindPopup(`<b>${p.name||"Fence"}</b><br>Circle ${p.radius_m||0}m`));
            bounds.extend([lat,lng]);
          }
        } else if (p.type==="POLYGON"&&f.geometry?.type==="Polygon") {
          const ring=(f.geometry.coordinates?.[0]||[]).map(([ln,la])=>[Number(la),Number(ln)]).filter(([la,ln])=>Number.isFinite(la)&&Number.isFinite(ln));
          if (ring.length) { gfShapes.push(L.polygon(ring,{color:"#ef4444",weight:2,fillOpacity:0.06}).bindPopup(`<b>${p.name||"Fence"}</b><br>Polygon`)); ring.forEach(c=>bounds.extend(c)); }
        }
      }
      if (gfShapes.length) geofenceLayerRef.current = L.layerGroup(gfShapes).addTo(map);
    }

    staticLayersRef.current = true;

    if (bounds.isValid() && !didInitialFitRef.current) {
      map.fitBounds(bounds.pad(0.15));
      didInitialFitRef.current = true;
    }
  }, []);

  const drawLiveLayers = useCallback(data => {
    const map = mapRef.current;
    if (!map) return false;

    const guards     = data.gps?.guards||[];
    const completion = data.completion||null;

    // FIX-5: explicit completed check — INPROGRESS is never in this set
    const completionStatus = completion ? String(completion.status||"").toUpperCase() : null;
    const isCompleted      = completionStatus ? COMPLETED_STATUSES.has(completionStatus) : false;

    let latestLL    = null;
    let latestTsRaw = null;
    let hasTrack    = false;
    let posChanged  = false;

    for (const g of guards) {
      const rawTrack     = Array.isArray(g.track) ? g.track : [];
      const orderedTrack = cleanTrack(rawTrack);
      if (!orderedTrack.length) continue;

      hasTrack = true;

      const segments = [];
      let current = null;

      orderedTrack.forEach(p => {
        const t  = parseDbIst(p.ts);
        const ms = t ? t.getTime() : null;
        const pt = [p.lat, p.lng];

        if (!current) { current={points:[pt],firstTs:p.ts,lastTs:p.ts,lastMs:ms,lastPt:pt}; return; }

        const timeGap = ms!==null && current.lastMs!==null && ms-current.lastMs>GAP_MS;

        if (timeGap) {
          segments.push(current);
          current={points:[pt],firstTs:p.ts,lastTs:p.ts,lastMs:ms,lastPt:pt};
        } else {
          current.points.push(pt);
          current.lastTs=p.ts;
          current.lastMs=ms!==null?ms:current.lastMs;
          current.lastPt=pt;
        }
      });
      if (current?.points.length) segments.push(current);
      if (!segments.length) continue;

      const allPoints = segments.flatMap(s=>s.points);
      const firstTs   = segments[0].firstTs;
      const lastSeg   = segments[segments.length-1];
      const lastTs    = lastSeg.lastTs;
      const firstPt   = allPoints[0];
      const lastPt    = allPoints[allPoints.length-1];
      const trackSig  = `${allPoints.length}-${lastTs}`;
      const posKey    = `${lastPt[0].toFixed(6)},${lastPt[1].toFixed(6)},${lastTs}`;

      if (posKey !== lastPositionKeyRef.current) {
        posChanged = true;
        lastPositionKeyRef.current = posKey;
      }

      const needsRedraw = trackSig!==lastTrackSigRef.current || !userPathLayerRef.current || !map.hasLayer(userPathLayerRef.current);

      if (needsRedraw) {
        if (userPathLayerRef.current)    { map.removeLayer(userPathLayerRef.current);    userPathLayerRef.current=null; }
        if (loginLogoutLayerRef.current) { map.removeLayer(loginLogoutLayerRef.current); loginLogoutLayerRef.current=null; }

        // FIX-7: catmullRomSpline handles simplification internally for large sets
        const polylines = segments
          .filter(s=>s.points.length>=2)
          .map(s=>L.polyline(catmullRomSpline(s.points,6),{weight:5,opacity:0.95,color:"#0ea5e9",smoothFactor:1}));

        const dots = segments
          .filter(s=>s.points.length===1)
          .map(s=>L.circleMarker(s.points[0],{radius:5,color:"#0ea5e9",fillColor:"#0ea5e9",fillOpacity:0.9}));

        const llMarkers=[];
        llMarkers.push(L.marker(firstPt,{icon:loginIcon()}).bindTooltip(`Login<br>${formatISTDateTime(firstTs)}`,{direction:"top"}));
        if (isCompleted) llMarkers.push(L.marker(lastPt,{icon:logoutIcon()}).bindTooltip(`Logout<br>${formatISTDateTime(completion?.completed_at||lastTs)}`,{direction:"top"}));

        userPathLayerRef.current    = L.layerGroup([...polylines,...dots]).addTo(map);
        loginLogoutLayerRef.current = L.layerGroup(llMarkers).addTo(map);
        lastTrackSigRef.current     = trackSig;
      }

      const idText   = g.guard_id||g.member_id||"";
      const nameText = g.guard_name||g.guard_label||idText||"User";
      const timeIST  = formatISTDateTime(lastTs);
      const lines    = [];
      if (nameText)                  lines.push(`Name: ${nameText}`);
      if (idText&&idText!==nameText) lines.push(`ID: ${idText}`);
      if (timeIST)                   lines.push(`Time: ${timeIST}`);
      const tooltip = lines.join("<br>");

      if (isCompleted) {
        if (latestMarkerRef.current) { map.removeLayer(latestMarkerRef.current); latestMarkerRef.current=null; }
      } else {
        if (!latestMarkerRef.current) {
          latestMarkerRef.current = L.marker(lastPt,{icon:userIcon("#2563EB",ICON.userSize),zIndexOffset:1200}).bindTooltip(tooltip,{direction:"top"}).addTo(map);
        } else {
          // FIX-6: threshold is now 0.0000001 — slow walking triggers animation
          animateMarker(latestMarkerRef.current, lastPt);
          latestMarkerRef.current.setTooltipContent(tooltip);
        }
      }

      latestLL    = [lastPt[0], lastPt[1]];
      latestTsRaw = lastTs;
    }

    const guardInfoEl = document.getElementById("guardInfo");
    if (guardInfoEl) {
      const g=guards[0];
      if (g) {
        const id=g.guard_id||g.member_id||"", name=g.guard_name||g.guard_label||id||"";
        guardInfoEl.textContent=`Guard: ${name&&id&&name!==id?`${name} (${id})`:name||id||"—"}`;
      } else {
        guardInfoEl.textContent=`Guard: ${data.allocation?.guard_member_id||queryData.userId||"—"}`;
      }
    }

    const sessionRangeEl = document.getElementById("sessionRange");
    if (sessionRangeEl) {
      const alloc=data.allocation||null, range=data.range||null;
      const s=alloc?.start_time||range?.start||null, e=alloc?.end_time||range?.end||null;
      sessionRangeEl.textContent=`Session: ${s?formatISTDateTime(s):"—"} → ${e?formatISTDateTime(e):"—"}`;
    }

    const lastTsEl = document.getElementById("lastTs");
    if (lastTsEl) lastTsEl.textContent=latestTsRaw?`Last update: ${formatISTFromUtc(latestTsRaw)}`:"Last update: —";

    const pollEl = document.getElementById("pollIndicator");
    if (pollEl) pollEl.textContent=`↻ ${(currentPollIntervalRef.current/1000).toFixed(1)}s`;

    if (completionStatus) applyCompletionStatus(completion);
    else if (hasTrack)    routeStatusSet("Route status: In progress","neutral");
    else                  routeStatusSet("Route status: Pending","neutral");

    const gf=data.geofences?.features||[];
    let inside=false;
    if (latestLL && Array.isArray(gf) && gf.length) {
      const [lat,lng]=latestLL;
      for (const f of gf) {
        const p=f.properties||{};
        if (p.type==="CIRCLE"&&f.geometry?.type==="Point") {
          const [clng,clat]=f.geometry.coordinates||[];
          if (Number.isFinite(Number(clat))&&Number.isFinite(Number(clng))&&insideCircle(lat,lng,Number(clat),Number(clng),p.radius_m||0)){inside=true;break;}
        } else if (p.type==="POLYGON"&&f.geometry?.type==="Polygon") {
          const ring=(f.geometry.coordinates?.[0]||[]).map(([ln,la])=>[Number(la),Number(ln)]).filter(([la,ln])=>Number.isFinite(la)&&Number.isFinite(ln));
          if (ring.length&&pointInPolygon(lat,lng,ring)){inside=true;break;}
        }
      }
    }
    if (!Array.isArray(gf)||!gf.length)   statusSet("No fence","neutral");
    else if (!latestLL)                    statusSet("No live location","neutral");
    else if (inside)                       statusSet("Inside geofence","ok");
    else                                   statusSet("Outside geofence","warn");

    return posChanged;
  }, [applyCompletionStatus, routeStatusSet, statusSet, queryData.userId]);

  const refresh = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current=true;
    let posChanged=false, isCompleted=false;

    try {
      const response    = await fetch(apiUrl,{cache:"no-store"});
      const contentType = response.headers.get("content-type")||"";
      if (!contentType.includes("application/json")) throw new Error(`API returned non-JSON (${response.status})`);
      const data = await response.json();
      if (!response.ok||!data.ok) throw new Error(data?.error||`HTTP ${response.status}`);

      drawStaticLayers(data);
      posChanged  = drawLiveLayers(data);

      // FIX-5: use COMPLETED_STATUSES set for the poll interval decision too
      isCompleted = COMPLETED_STATUSES.has(String(data.completion?.status||"").toUpperCase());
    } catch (error) {
      statusSet(error.message||"API error","warn");
      console.error("[routeTracking] fetch failed:", error);
    } finally {
      isFetchingRef.current=false;

      if (isCompleted) {
        currentPollIntervalRef.current = POLL_SLOW_MS;
      } else if (posChanged) {
        currentPollIntervalRef.current = POLL_FAST_MS;
      } else {
        currentPollIntervalRef.current = Math.min(currentPollIntervalRef.current+500, POLL_SLOW_MS);
      }

      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = setTimeout(()=>refresh(), currentPollIntervalRef.current);
    }
  }, [apiUrl, drawStaticLayers, drawLiveLayers, statusSet]);

  useEffect(() => {
    if (!queryData.routeId) return;

    if (mapRef.current) { mapRef.current.remove(); mapRef.current=null; }

    // FIX-8: null out all layer refs on cleanup so stale refs can't linger
    latestMarkerRef.current     = null;
    userPathLayerRef.current    = null;
    loginLogoutLayerRef.current = null;
    checkpointsLayerRef.current = null;
    geofenceLayerRef.current    = null;
    routeLineLayerRef.current   = null;
    routeEndPointsRef.current   = null;
    staticLayersRef.current     = false;
    didInitialFitRef.current    = false;
    lastTrackSigRef.current     = "";
    lastPositionKeyRef.current  = "";
    currentPollIntervalRef.current = POLL_FAST_MS;

    const map = L.map("map",{zoomControl:true}).setView([20.5937,78.9629],5);
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",{maxZoom:20,attribution:"&copy; OpenStreetMap"}).addTo(map);
    L.control.scale({imperial:false}).addTo(map);

    const legend = L.control({position:"bottomright"});
    legend.onAdd=()=>{
      const d=L.DomUtil.create("div","legend");
      d.innerHTML=`
        <div><span class="chip" style="background:#0ea5e9"></span>Track</div>
        <div><span class="chip" style="background:#ef4444"></span>Fence (Polygon)</div>
        <div><span class="chip" style="background:#f59e0b"></span>Fence (Circle)</div>
        <div><span class="chip" style="background:#22c55e"></span>Checkpoints</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#16a34a;border-radius:3px;"></span> From</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#dc2626;border-radius:3px;"></span> To</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#2563EB;border-radius:50%;"></span> Live user</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#16a34a;border-radius:999px;"></span> Login</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#8b5cf6;border-radius:999px;"></span> Logout</div>`;
      return d;
    };
    legend.addTo(map);

    refresh();

    return () => {
      if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current=null; }
      if (mapRef.current) { mapRef.current.remove(); mapRef.current=null; }
      // FIX-8: null refs on unmount
      latestMarkerRef.current     = null;
      userPathLayerRef.current    = null;
      loginLogoutLayerRef.current = null;
      checkpointsLayerRef.current = null;
      geofenceLayerRef.current    = null;
      routeLineLayerRef.current   = null;
      routeEndPointsRef.current   = null;
    };
  }, [queryData.routeId, refresh]);

  if (!queryData.routeId) {
    return (
      <Layout dashboardData={dashboardData} routePage={routePage} activePage="tracking" activeMenu="tracking">
        <div className="p-5 text-slate-700">route_id is required. Example: <b>?route_id=123</b></div>
      </Layout>
    );
  }

  return (
    <Layout dashboardData={dashboardData} routePage={routePage} activePage="tracking" activeMenu="tracking">
      <style>{`
        #map { height:100%; width:100%; min-height:520px; }
        .legend { background:#fff; padding:10px 12px; border-radius:10px; box-shadow:0 2px 12px rgba(0,0,0,.12); font:12px/1.25 system-ui; }
        .legend div { margin:6px 0; display:flex; align-items:center; gap:8px; }
        .chip { display:inline-block; width:14px; height:14px; border-radius:3px; }
        .pin { transform:translate(-50%,-100%); }
        .userIcon { transform:translate(-50%,-50%); }
        .pulse { position:absolute; top:50%; left:50%; width:18px; height:18px; border-radius:999px; transform:translate(-50%,-50%); animation:pulse 1.6s infinite; }
        @keyframes pulse { 0%{box-shadow:0 0 0 0 rgba(37,99,235,.55)} 70%{box-shadow:0 0 0 18px rgba(37,99,235,0)} 100%{box-shadow:0 0 0 0 rgba(37,99,235,0)} }
      `}</style>

      <div className="h-[calc(100vh-90px)] min-h-[650px] bg-slate-100 rounded-2xl overflow-hidden border border-slate-200 shadow-sm flex flex-col">
        <header className="bg-white border-b border-slate-200 px-4 py-3 flex flex-wrap items-center gap-3">
          <h3 className="m-0 text-lg font-semibold text-slate-900">
            Route #{queryData.routeId}
            {queryData.allocId ? ` — Allocation #${queryData.allocId}` : ""}
          </h3>
          <div id="guardInfo"     className="text-sm text-slate-700">Guard: —</div>
          <div id="sessionRange"  className="text-xs text-slate-600">Session: —</div>
          <div id="routeStatus"   className="px-3 py-1 text-xs font-semibold rounded-full bg-slate-200 text-slate-800">Route status: —</div>
          <div id="fenceStatus"   className="px-3 py-1 text-xs font-semibold rounded-full bg-slate-200 text-slate-800">No fence</div>
          <div id="lastTs"        className="ml-auto text-xs text-slate-500">—</div>
          <div id="pollIndicator" className="px-2 py-1 text-xs font-semibold rounded-full bg-emerald-600 text-white">↻ 3.0s</div>
        </header>
        <div className="flex-1"><div id="map"/></div>
      </div>
    </Layout>
  );
}